import { useEffect, useState, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useCompany } from "../../../contexts/CompanyContext";
import { useAuth } from "../../../contexts/AuthContext";
import { getRepTier } from "./RankUpModal";

interface MissionDef {
  type: "tasks" | "hubs" | "capital";
  label: string;
  target: number;
  unit: string;
  icon: string;
}

interface RankMissions {
  [level: number]: MissionDef[];
}

const RANK_MISSIONS: RankMissions = {
  1: [ // 인턴 → 사원 (rep 21)
    { type: "tasks", label: "허브 태스크 완료", target: 3, unit: "건", icon: "ri-checkbox-line" },
  ],
  2: [ // 사원 → 대리 (rep 41)
    { type: "tasks", label: "허브 태스크 완료", target: 8, unit: "건", icon: "ri-checkbox-line" },
    { type: "hubs", label: "허브 다양성 (서로 다른 허브)", target: 2, unit: "개 허브", icon: "ri-layout-grid-line" },
  ],
  3: [ // 대리 → 과장 (rep 61)
    { type: "tasks", label: "허브 태스크 완료", target: 16, unit: "건", icon: "ri-checkbox-line" },
    { type: "hubs", label: "허브 다양성 (서로 다른 허브)", target: 3, unit: "개 허브", icon: "ri-layout-grid-line" },
    { type: "capital", label: "자본금 달성", target: 10000, unit: "만원 이상", icon: "ri-money-dollar-circle-line" },
  ],
  4: [ // 과장 → 부장 (rep 81)
    { type: "tasks", label: "허브 태스크 완료", target: 28, unit: "건", icon: "ri-checkbox-line" },
    { type: "hubs", label: "허브 다양성 (서로 다른 허브)", target: 4, unit: "개 허브", icon: "ri-layout-grid-line" },
    { type: "capital", label: "자본금 달성", target: 35000, unit: "만원 이상", icon: "ri-money-dollar-circle-line" },
  ],
};

const NEXT_REP: Record<number, number> = { 1: 21, 2: 41, 3: 61, 4: 81 };

const HUB_NAMES: Record<string, string> = {
  ar: "A&R", "ar-hub": "A&R", arHub: "A&R",
  concert: "공연기획", "concert-hub": "공연기획",
  foundation: "재단", "foundation-hub": "재단",
  ip: "IP 스튜디오", "ip-hub": "IP 스튜디오",
  campaign: "캠페인", "campaign-hub": "캠페인",
};

function normalizeHub(hub: string): string {
  const k = hub?.toLowerCase().replace(/-hub$/, "").replace(/hub$/, "").trim();
  return HUB_NAMES[k] ?? hub;
}

interface CompletionData {
  totalTasks: number;
  uniqueHubs: number;
  hubBreakdown: { hub: string; count: number }[];
}

export default function RankMissionPanel() {
  const { companyState, workspaceId } = useCompany();
  const { user } = useAuth();
  const [completionData, setCompletionData] = useState<CompletionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const fetchCompletions = useCallback(async () => {
    if (!user || !workspaceId) return;
    const { data } = await supabase
      .from("task_completions")
      .select("task_id, hub")
      .eq("workspace_id", workspaceId)
      .eq("user_id", user.id);

    const rows = data ?? [];
    const hubCount: Record<string, number> = {};
    rows.forEach(r => {
      const norm = normalizeHub(r.hub ?? "기타");
      hubCount[norm] = (hubCount[norm] ?? 0) + 1;
    });
    const uniqueHubs = Object.keys(hubCount).length;
    const hubBreakdown = Object.entries(hubCount)
      .map(([hub, count]) => ({ hub, count }))
      .sort((a, b) => b.count - a.count);

    setCompletionData({ totalTasks: rows.length, uniqueHubs, hubBreakdown });
    setLoading(false);
  }, [user, workspaceId]);

  useEffect(() => { fetchCompletions(); }, [fetchCompletions]);

  if (!companyState) return null;

  const reputation = companyState.reputation;
  const capital = companyState.capital;
  const tier = getRepTier(reputation);
  const isMax = tier.level >= 5;

  if (isMax) {
    return (
      <div className="bg-[#111112] border border-yellow-500/20 rounded-xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-400/15">
          <i className="ri-vip-crown-fill text-yellow-400 text-lg"></i>
        </div>
        <div>
          <div className="text-yellow-400 text-sm font-bold">최고 직급 달성!</div>
          <div className="text-[#555555] text-xs">부장 직급 — K-ArtPEN의 정점에 올랐습니다</div>
        </div>
      </div>
    );
  }

  const missions = RANK_MISSIONS[tier.level] ?? [];
  const nextRep = NEXT_REP[tier.level] ?? 100;
  const nextTier = getRepTier(nextRep);
  const repToGo = Math.max(0, nextRep - reputation);

  const getMissionProgress = (mission: MissionDef) => {
    if (!completionData || !companyState) return { value: 0, done: false };
    switch (mission.type) {
      case "tasks": {
        const v = completionData.totalTasks;
        return { value: v, done: v >= mission.target };
      }
      case "hubs": {
        const v = completionData.uniqueHubs;
        return { value: v, done: v >= mission.target };
      }
      case "capital": {
        const v = capital;
        return { value: v, done: v >= mission.target };
      }
      default:
        return { value: 0, done: false };
    }
  };

  const allMissionsDone = missions.every(m => getMissionProgress(m).done);
  const repAchieved = reputation >= nextRep;
  const canPromote = allMissionsDone && repAchieved;

  return (
    <div className="bg-[#111112] rounded-xl border border-white/[0.05] overflow-hidden">
      {/* 헤더 */}
      <button
        type="button"
        onClick={() => setCollapsed(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-7 h-7 flex items-center justify-center rounded-lg"
            style={{ backgroundColor: `${tier.color}15` }}
          >
            <i className={`${tier.icon} text-sm`} style={{ color: tier.color }}></i>
          </div>
          <div className="text-left">
            <div className="text-[#CCCCCC] text-sm font-semibold">승급 미션</div>
            <div className="text-[#444444] text-[10px]">
              <span style={{ color: tier.color }}>{tier.name}</span>
              <span className="mx-1.5">→</span>
              <span style={{ color: nextTier.color }}>{nextTier.name}</span>으로 승급하려면
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canPromote && (
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-400 border border-emerald-400/30">
              승급 가능!
            </span>
          )}
          <i className={`ri-arrow-${collapsed ? "down" : "up"}-s-line text-[#555555] text-sm`}></i>
        </div>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/[0.04] pt-3">
          {/* 직급 점수 진행도 */}
          <div className="bg-white/[0.02] rounded-lg px-3 py-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[#888888] text-[10px] flex items-center gap-1">
                <i className="ri-bar-chart-line text-xs"></i>직급 점수
              </span>
              <span className="text-[10px] tabular-nums" style={{ color: repToGo === 0 ? "#00C896" : tier.color }}>
                {reputation}pt / {nextRep}pt 필요
              </span>
            </div>
            <div className="w-full h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${Math.min(100, (reputation / nextRep) * 100)}%`,
                  background: `linear-gradient(90deg, ${tier.color}80, ${tier.color})`,
                }}
              />
            </div>
            {repToGo > 0 && (
              <div className="text-[#333333] text-[9px] mt-1">
                직급점수 <span className="text-[#555555]">{repToGo}pt</span> 추가 필요 — 이벤트 대응, 태스크 완료로 획득
              </div>
            )}
          </div>

          {/* 미션 목록 */}
          {loading ? (
            <div className="space-y-2">
              {[...Array(missions.length)].map((_, i) => (
                <div key={i} className="h-12 bg-white/[0.02] rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {missions.map((mission, idx) => {
                const { value, done } = getMissionProgress(mission);

                let displayVal = "";
                let progressPct = 0;
                if (mission.type === "tasks") {
                  displayVal = `${value}건 / ${mission.target}건`;
                  progressPct = Math.min(100, (value / mission.target) * 100);
                } else if (mission.type === "hubs") {
                  displayVal = `${value}개 / ${mission.target}개`;
                  progressPct = Math.min(100, (value / mission.target) * 100);
                } else if (mission.type === "capital") {
                  displayVal = `${value.toLocaleString()}만 / ${mission.target.toLocaleString()}만`;
                  progressPct = Math.min(100, (value / mission.target) * 100);
                }

                return (
                  <div
                    key={idx}
                    className={`rounded-lg border px-3 py-2.5 transition-all ${
                      done
                        ? "bg-emerald-400/[0.04] border-emerald-400/20"
                        : "bg-white/[0.01] border-white/[0.05]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 flex items-center justify-center rounded ${done ? "bg-emerald-400/20" : "bg-white/[0.05]"}`}>
                          {done
                            ? <i className="ri-check-line text-emerald-400 text-xs"></i>
                            : <i className={`${mission.icon} text-[#555555] text-xs`}></i>
                          }
                        </div>
                        <span className={`text-xs ${done ? "text-emerald-400" : "text-[#888888]"}`}>
                          {mission.label}
                        </span>
                      </div>
                      <span className={`text-[10px] tabular-nums font-medium ${done ? "text-emerald-400" : "text-[#555555]"}`}>
                        {displayVal}
                      </span>
                    </div>

                    {!done && (
                      <div className="w-full h-1 bg-white/[0.05] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${progressPct}%`,
                            backgroundColor: progressPct >= 80 ? "#00C896" : progressPct >= 40 ? "#FF9F43" : "#4B6CF7",
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 허브별 완료 현황 */}
          {!loading && completionData && completionData.hubBreakdown.length > 0 && (
            <div className="bg-white/[0.01] rounded-lg px-3 py-2.5 border border-white/[0.04]">
              <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-2">허브별 완료</div>
              <div className="flex flex-wrap gap-1.5">
                {completionData.hubBreakdown.map(h => (
                  <div key={h.hub} className="flex items-center gap-1.5 px-2 py-1 bg-white/[0.03] rounded-md border border-white/[0.04]">
                    <span className="text-[#888888] text-[10px]">{h.hub}</span>
                    <span className="text-[10px] font-bold text-[#4B6CF7]">{h.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 승급 가능 알림 */}
          {canPromote && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-400/[0.06] border border-emerald-400/20">
              <i className="ri-checkbox-circle-fill text-emerald-400 text-sm shrink-0"></i>
              <div>
                <div className="text-emerald-400 text-xs font-semibold">모든 조건 달성!</div>
                <div className="text-[#444444] text-[10px]">강사가 직급 점수를 확인하면 승급이 인정됩니다</div>
              </div>
            </div>
          )}

          {/* 힌트 */}
          {!canPromote && !loading && (
            <div className="text-[#333333] text-[10px] leading-relaxed">
              <i className="ri-lightbulb-line text-[#3D3D3D] mr-1"></i>
              이벤트 위기 대응, 허브 태스크 완료로 직급 점수와 자본금을 획득하세요
            </div>
          )}
        </div>
      )}
    </div>
  );
}
